import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Close } from "@dreamhub/designsystem/icons";
import { isEmpty } from "lodash";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import TextEditorWithActions from "@/components/controls/TextEditor/TextEditorWithActions";
import { useCreateNextStepMutation } from "@/pages/deals/hooks";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { NextStepsResponse } from "@/pages/deals/types";

interface DealNextStepsProps {
  dealId: string;
  setHasNextStepsInternal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<NextStepsResponse, Error>>;
}

const AddNewStepSection: React.FC<DealNextStepsProps> = ({
  dealId,
  setHasNextStepsInternal,
  setIsExpanded,
  refetch
}) => {
  const { t } = useTranslation();
  const [isAddingNewStep, setIsAddingNewStep] = useState(false);

  const createNextStep = useCreateNextStepMutation();

  const handleAddNewClick = () => {
    setIsAddingNewStep(true);
  };

  const handleCancelAddNew = () => {
    setIsAddingNewStep(false);
  };

  const handleCreateNewStep = (value: string) => {
    const trimmedValue = value.trim();
    if (isEmpty(trimmedValue)) return null;
    createNextStep.mutate(
      {
        pathParams: { dealId },
        data: {
          description: trimmedValue
        }
      },
      {
        onSuccess: () => {
          refetch();
          setHasNextStepsInternal(true);
          setIsExpanded(true);
          setIsAddingNewStep(false);
        }
      }
    );
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="transition-all duration-300 ease-in-out w-full"
    >
      {!isAddingNewStep && (
        <Button
          variant="secondaryNoBg"
          className="text-link border-none"
          onClick={handleAddNewClick}
        >
          <IconContainer size="5" color="current">
            <Add />
          </IconContainer>
          <span>{t("deals.nextSteps.addNewSteps")}</span>
        </Button>
      )}
      {isAddingNewStep && (
        <div className="flex flex-col w-full">
          <Button
            variant="secondaryNoBg"
            className="border-none w-fit font-normal"
            onClick={handleCancelAddNew}
          >
            <IconContainer>
              <Close />
            </IconContainer>
            <span>{t("deals.nextSteps.addNewSteps")}</span>
          </Button>
          <TextEditorWithActions
            placeholder={t("deals.nextSteps.addNewSteps")}
            onSave={handleCreateNewStep}
            onCancel={handleCancelAddNew}
            showActions
            startInEditMode={true}
            clearOnSave
            width="100%"
            className="!w-full !block p-2"
            initialValue=""
            minRows={5}
            showFocusState
          />
        </div>
      )}
    </div>
  );
};

export default AddNewStepSection;
